import { useState } from "react";

import { Button } from "src/base-components/Button";
import { Label } from "src/base-components/Label";
import {
  AssigneeSelect,
  AssigneeSelectOption,
} from "src/cases/queues/AssigneeSelect";
import { useQueueMembers } from "src/cases/queues/hooks";
import { Modal } from "src/design-system/Modal";
import { CasesPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const NO_ASSIGNEE_OPTION = {
  key: "none",
  value: "No assignee",
} as const;

export const CasesBulkAssignModal: React.FC<{
  isOpen: boolean;
  isAssigning?: boolean;
  onClose: () => void;
  onAssign: (assigneeId: string | null) => Promise<void>;
}> = ({ isOpen, onClose, onAssign, isAssigning }) => {
  const { queue_id } = useParamsDecode<CasesPageParams>();
  const [assignee, setAssignee] = useState<Nullable<string>>(null);
  const { data: users, isLoading: isLoadingMembers } =
    useQueueMembers(queue_id);

  const usersOptions: AssigneeSelectOption[] = [
    NO_ASSIGNEE_OPTION,
    ...(users?.map((user) => ({ key: user.id, user })) ?? []),
  ];

  const selectedAssignee =
    assignee === NO_ASSIGNEE_OPTION["key"] ? null : assignee;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header description="Assign selected cases to a reviewer in bulk">
        Bulk assign cases
      </Modal.Header>
      <Modal.Content>
        <div>
          <Label>Select an assignee</Label>
          <AssigneeSelect
            dataLoc="cases-bulk-assign-select"
            disabled={isLoadingMembers || isAssigning}
            loading={isLoadingMembers}
            options={usersOptions}
            value={assignee}
            onChange={setAssignee}
          />
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="cases-bulk-assign-confirm-button"
            disabled={isAssigning || isLoadingMembers}
            variant="primary"
            onClick={() => onAssign(selectedAssignee)}
          >
            Assign cases
          </Button>
        }
        secondaryButton={
          <Button
            disabled={isAssigning}
            size="base"
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
        }
      />
    </Modal>
  );
};
